// src/components/common/Layout/MainLayout.jsx
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  BookOpen,
  FileSpreadsheet,
  Menu,
  X,
} from 'lucide-react';
import AuthService from '../../../services/authService';
import { BRAND_CONFIG, getLogoSrc } from '../../../config/brandConfig';
import { useCompany } from '../../../contexts/CompanyContext';
import { useAppTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { ThemeToggle, LanguageToggle } from '../UiControls';

const getNavigationItems = (role, t) => {
  const dashboardItem = { text: t('nav.dashboard'), icon: LayoutDashboard, path: '/edefter/dashboard' };

  const roleBasedItems = {
    Admin: [
      dashboardItem,
      { text: t('nav.addCompany'), icon: Building2, path: '/edefter/add-company' },
      { text: t('nav.generalJournal'), icon: BookOpen, path: '/edefter/general-journal' },
      { text: t('nav.trialBalance'), icon: FileSpreadsheet, path: '/edefter/trial-balance' },
    ],
    User: [
      dashboardItem,
    ],
  };

  return roleBasedItems[role] || roleBasedItems.User;
};

const MainLayout = ({
  children,
  user = { name: 'John Doe', role: 'User', email: 'john@example.com' },
  contentMaxWidth = 'max-w-6xl',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode } = useAppTheme();
  const { t } = useLanguage();
  const { selectedCompany } = useCompany();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = getNavigationItems(user.role, t);
  const logoSrc = getLogoSrc(darkMode);

  const getPageLabel = (pathname) => {
    const labels = {
      '/edefter/dashboard': t('nav.dashboard'),
      '/edefter/add-company': t('nav.addCompany'),
      '/edefter/general-journal': t('nav.generalJournal'),
      '/edefter/trial-balance': t('nav.trialBalance'),
      '/edefter/upload': t('nav.upload'),
      '/edefter/mizan-reading': t('nav.mizanReading'),
      '/profile': t('nav.profile'),
      '/change-password': t('nav.changePassword'),
    };
    const match = Object.entries(labels).find(
      ([path]) => pathname === path || pathname.startsWith(`${path}/`)
    );
    return match ? match[1] : 'Fidost';
  };

  const pageLabel = getPageLabel(location.pathname);

  const isActiveRoute = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    AuthService.logout(true);
  };

  const userInitials = () => {
    if (user.name && user.surname)
      return `${user.name.charAt(0)}${user.surname.charAt(0)}`.toUpperCase();
    if (user.email) return user.email.substring(0, 2).toUpperCase();
    return 'U';
  };

  /* ── Shared nav item renderer ── */
  const NavItem = ({ item, onAfterClick }) => {
    const Icon = item.icon;
    const active = isActiveRoute(item.path);

    const handleClick = () => {
      if (item.externalUrl) {
        const url = typeof item.externalUrl === 'function' ? item.externalUrl() : item.externalUrl;
        window.location.href = url;
      } else {
        handleNavigation(item.path);
      }
      onAfterClick?.();
    };

    return (
      <button
        key={item.path}
        type="button"
        onClick={handleClick}
        className={`w-full flex items-center p-3 rounded-xl transition-all duration-200 group ${
          active
            ? 'bg-[#24299d] text-white shadow-lg shadow-[#24299d]/20'
            : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10 dark:text-gray-400'
        }`}
      >
        <Icon
          size={20}
          className={
            active
              ? 'text-white'
              : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-200'
          }
        />
        <span className="ml-3 font-medium text-sm">{item.text}</span>
      </button>
    );
  };

  /* ── Sidebar footer (profile + controls) — same in desktop & mobile ── */
  const SidebarFooter = ({ onAfterClick }) => (
    <div className="p-4 border-t border-gray-100 dark:border-white/5">
      {/* Profile row — click goes directly to profile page */}
      <button
        type="button"
              onClick={() => {
          handleNavigation('/profile');
          onAfterClick?.();
        }}
        className="w-full flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 transition-colors cursor-pointer group"
      >
        <div className="flex-none w-10 h-10 rounded-full bg-gradient-to-tr from-[#24299d] to-[#3840c4] flex items-center justify-center text-white text-sm font-bold">
          {userInitials()}
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="text-xs font-medium group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
            {user.email || 'User'}
          </div>
          <div className="text-[10px] text-gray-500 truncate">
            {selectedCompany?.name ? (
              selectedCompany.name
            ) : (
              <span className="text-red-600 font-semibold">
                {t('nav.noCompanySelected')}
              </span>
            )}
          </div>
        </div>
      </button>

      {/* Controls: language + theme toggles and logout */}
      <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-white/5">
        <div className="flex items-center gap-2">
          <LanguageToggle />
          <ThemeToggle />
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 text-xs font-semibold hover:bg-gray-200 dark:hover:bg-white/20 hover:text-gray-800 dark:hover:text-white transition-colors"
        >
          {t('nav.logout')}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden flex-col md:flex-row bg-[#F2F2F7] dark:bg-black text-gray-900 dark:text-white transition-colors duration-300 font-sans">
      {/* ── Desktop Sidebar ── */}
      <aside className="hidden md:flex w-64 flex-shrink-0 bg-white/80 dark:bg-[#1C1C1E]/80 backdrop-blur-xl border-r border-gray-200 dark:border-white/10 flex-col justify-between z-50">
        <div>
          <div className="h-20 flex items-center justify-start px-4 border-b border-gray-100 dark:border-white/5">
            <img
              src={logoSrc}
              alt={BRAND_CONFIG.name}
              className="w-56 h-20 object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </nav>
        </div>
        <SidebarFooter />
      </aside>

      {/* ── Mobile overlay ── */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden
        />
      )}

      {/* ── Mobile Sidebar ── */}
      <aside
        className={`md:hidden fixed top-0 left-0 h-full w-72 bg-white dark:bg-[#1C1C1E] z-50 transform transition-transform duration-300 ease-in-out ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } shadow-2xl`}
      >
        <div className="flex flex-col h-full">
          <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 dark:border-white/10">
            <div className="flex items-center gap-3 min-w-0">
              <img src={logoSrc} alt={BRAND_CONFIG.name} className="w-10 h-10 object-contain" />
              <span className="text-lg font-bold truncate">{BRAND_CONFIG.shortName}</span>
            </div>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            >
              <X size={20} className="text-gray-600 dark:text-gray-400" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto p-4 space-y-2">
            {navItems.map((item) => (
              <NavItem
                key={item.path}
                item={item}
                onAfterClick={() => setMobileMenuOpen(false)}
              />
            ))}
          </nav>
          <SidebarFooter onAfterClick={() => setMobileMenuOpen(false)} />
        </div>
      </aside>

      {/* ── Main content ── */}
      <main className="flex-1 flex flex-col h-full relative overflow-hidden">
        {/* Header — only mobile hamburger + page title, no company selector, no toggles */}
        <header className="h-14 md:h-16 flex items-center gap-3 px-3 md:px-8 bg-[#F2F2F7]/80 dark:bg-black/80 backdrop-blur-md sticky top-0 z-40">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 transition-colors shrink-0"
          >
            <Menu size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
          <div className="min-w-0">
            <h1 className="text-base md:text-xl font-bold tracking-tight truncate leading-tight">
              {selectedCompany?.name ?? t('nav.noCompanySelected')}
            </h1>
            <p className="text-[11px] md:text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide truncate leading-tight">
              {pageLabel}
              {selectedCompany?.vergiKimlikNo ? ` · ${selectedCompany.vergiKimlikNo}` : ''}
            </p>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto no-scrollbar p-3 md:p-8">
          <div className={`${contentMaxWidth} mx-auto w-full`}>{children}</div>
        </div>
      </main>
    </div>
  );
};

export default MainLayout;
